import type {
  ImpactAnalysisCalculatedResult,
  ImpactAnalysisData,
  ImpactLevel,
} from "@/types/impact-analysis";

export function evaluate9b1(
  personalDataAffected: number | null
): ImpactLevel | null {
  if (personalDataAffected === null || personalDataAffected === undefined) {
    return null;
  }
  if (personalDataAffected < 200000) return "SCĂZUT";
  if (personalDataAffected <= 1000000) return "MEDIU";
  return "RIDICAT";
}

export function evaluate9b2(
  publicServiceAccessLost: number | null
): ImpactLevel | null {
  if (publicServiceAccessLost === null || publicServiceAccessLost === undefined)
    return null;
  if (publicServiceAccessLost < 25000) return "SCĂZUT";
  if (publicServiceAccessLost < 115000) return "MEDIU";
  return "RIDICAT";
}

export function evaluate9b3(
  gdpLossOverThreshold: boolean | null
): ImpactLevel | null {
  if (gdpLossOverThreshold === null || gdpLossOverThreshold === undefined)
    return null;
  return gdpLossOverThreshold ? "RIDICAT" : "SCĂZUT";
}

export function evaluate9b4(
  affectsOtherSectors: boolean | null,
  highestImpact: number | null
): ImpactLevel | null {
  if (
    affectsOtherSectors === null ||
    affectsOtherSectors === undefined ||
    highestImpact === null ||
    highestImpact === undefined
  ) {
    return null;
  }
  if (!affectsOtherSectors) return "SCĂZUT";
  if (affectsOtherSectors) {
    if (highestImpact >= 20) return "RIDICAT";
    if (highestImpact >= 5) return "MEDIU";
  }
  return "SCĂZUT";
}

export function evaluate9b5(
  criticalInfra: "no" | "affected" | "destroyed" | null
): ImpactLevel | null {
  if (criticalInfra === null || criticalInfra === undefined) return null;
  if (criticalInfra === "no") return "SCĂZUT";
  if (criticalInfra === "affected") return "MEDIU";
  if (criticalInfra === "destroyed") return "RIDICAT";
  return null;
}

export function evaluate9b6(canCauseDeath: boolean | null): ImpactLevel | null {
  if (canCauseDeath === null || canCauseDeath === undefined) return null;
  return canCauseDeath ? "RIDICAT" : "SCĂZUT";
}

export function evaluate9b7(chronicIllness: number | null): ImpactLevel | null {
  if (chronicIllness === null || chronicIllness === undefined) return null;
  if (chronicIllness > 50) return "RIDICAT";
  return "SCĂZUT";
}

export function evaluate9b8(
  traumaticInjuries: number | null
): ImpactLevel | null {
  if (traumaticInjuries === null || traumaticInjuries === undefined)
    return null;
  if (traumaticInjuries === 0) return "SCĂZUT";
  if (traumaticInjuries >= 115000) return "RIDICAT";
  return "MEDIU";
}

export function evaluate9b9(
  peopleFinancialLoss: number | null
): ImpactLevel | null {
  if (peopleFinancialLoss === null || peopleFinancialLoss === undefined)
    return null;
  if (peopleFinancialLoss < 25000) return "SCĂZUT";
  if (peopleFinancialLoss < 115000) return "MEDIU";
  return "RIDICAT";
}

export function evaluate9b10(
  affectsDefenseCapacity: "no" | "affected" | "destroyed" | null
): ImpactLevel | null {
  if (affectsDefenseCapacity === null || affectsDefenseCapacity === undefined)
    return null;
  if (affectsDefenseCapacity === "no") return "SCĂZUT";
  if (affectsDefenseCapacity === "affected") return "MEDIU";
  if (affectsDefenseCapacity === "destroyed") return "RIDICAT";
  return null;
}

export function evaluate9b11(
  generatesStateIncapacity: boolean | null
): ImpactLevel | null {
  if (
    generatesStateIncapacity === null ||
    generatesStateIncapacity === undefined
  )
    return null;
  return generatesStateIncapacity ? "RIDICAT" : "SCĂZUT";
}

export function evaluate9b12(
  damagesStateReputation: boolean | null
): ImpactLevel | null {
  if (damagesStateReputation === null || damagesStateReputation === undefined)
    return null;
  return damagesStateReputation ? "RIDICAT" : "SCĂZUT";
}

export function evaluate9c1(
  affectsOtherSectors: boolean | null,
  sectorsCount: number | null,
  highestImpact: number | null
): ImpactLevel | null {
  if (affectsOtherSectors === null || affectsOtherSectors === undefined) {
    return null;
  }
  if (!affectsOtherSectors) return "SCĂZUT";

  const levels: ImpactLevel[] = ["SCĂZUT"];

  if (highestImpact !== null && highestImpact !== undefined) {
    if (highestImpact >= 20) levels.push("RIDICAT");
    else if (highestImpact >= 5) levels.push("MEDIU");
  }

  if (sectorsCount !== null && sectorsCount !== undefined) {
    if (sectorsCount >= 5) levels.push("RIDICAT");
    else if (sectorsCount >= 3) levels.push("MEDIU");
  }

  if (levels.includes("RIDICAT")) return "RIDICAT";
  if (levels.includes("MEDIU")) return "MEDIU";
  return "SCĂZUT";
}

export function evaluate9c2(
  obligations: "no" | "affected" | "destroyed" | null
): ImpactLevel | null {
  if (obligations === null || obligations === undefined) return null;
  if (obligations === "no") return "SCĂZUT";
  if (obligations === "affected") return "MEDIU";
  if (obligations === "destroyed") return "RIDICAT";
  return null;
}

export function calculateSection2Risk(
  results: Array<ImpactLevel | null>
): ImpactLevel | null {
  const filtered = results.filter((r) => r !== null);
  if (filtered.length === 0) return null;
  if (filtered.includes("RIDICAT")) return "RIDICAT";
  if (filtered.includes("MEDIU")) return "MEDIU";
  return "SCĂZUT";
}

export function calculateSection3Risk(
  results: Array<ImpactLevel | null>
): ImpactLevel | null {
  const filtered = results.filter((r) => r !== null);
  if (filtered.length === 0) return null;
  if (filtered.includes("RIDICAT")) return "RIDICAT";
  if (filtered.includes("MEDIU")) return "MEDIU";
  return "SCĂZUT";
}

export function calculateOverallRisk(
  section2: ImpactLevel | null,
  section3: ImpactLevel | null
): ImpactLevel | null {
  if (section2 === null && section3 === null) return null;
  if (section2 === "RIDICAT" || section3 === "RIDICAT") return "RIDICAT";
  if (section2 === "MEDIU" || section3 === "MEDIU") return "MEDIU";
  return "SCĂZUT";
}

export function calculateImpactAnalysisResult(
  data: ImpactAnalysisData
): ImpactAnalysisCalculatedResult {
  const section2 = data.section2_public_impact;
  const section3 = data.section3_systemic_risk;

  const nineBResults = [
    evaluate9b1(section2.fundamental_rights.personal_data_affected),
    evaluate9b2(section2.fundamental_rights.public_service_access_lost),
    evaluate9b3(section2.national_economy.gdp_loss_over_threshold),
    evaluate9b4(
      section2.national_economy.affects_other_sector_services,
      section2.national_economy.highest_sector_impact_percentage
    ),
    evaluate9b5(section2.national_economy.affects_critical_infrastructure),
    evaluate9b6(section2.health_life.can_cause_death),
    evaluate9b7(section2.health_life.chronic_illness_or_disability),
    evaluate9b8(section2.health_life.traumatic_injuries_or_health_impact),
    evaluate9b9(section2.financial_impact.people_financial_loss),
    evaluate9b10(section2.defense_security.affects_defense_capacity),
    evaluate9b11(section2.defense_security.generates_state_incapacity),
    evaluate9b12(section2.defense_security.damages_state_reputation),
  ];

  const nineCResults = [
    evaluate9c1(
      section3.affects_other_sectors,
      section3.affected_sectors_count,
      section3.highest_impact_percentage
    ),
    evaluate9c2(section3.affects_international_obligations),
  ];

  return {
    article9a: data.section1_mandatory.is_sole_essential_provider,
    article9b: calculateSection2Risk(nineBResults),
    article9c: calculateSection3Risk(nineCResults),
    article9d: data.section1_mandatory.formally_designated,
  };
}
